import React, { useMemo, useEffect, useRef } from "react";
import { Flame, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Count consecutive days a user has journaled, ending today or yesterday.
 * A "day" is UTC-floored so mid-flight entries don't confuse it.
 */
export function computeStreak(entries = []) {
  if (!entries.length) return 0;

  const dayKey = (iso) => {
    const d = new Date(iso);
    return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
  };

  // Unique days sorted DESC
  const uniqueDays = [...new Set(entries.map((e) => dayKey(e.created_at)))].sort().reverse();
  const parseKey = (k) => {
    const [y, m, d] = k.split("-").map(Number);
    return new Date(Date.UTC(y, m, d));
  };

  const today = new Date();
  const todayKey = dayKey(today.toISOString());
  const yesterdayKey = dayKey(new Date(today.getTime() - 86400000).toISOString());

  // Streak must include today OR yesterday to count as "current"
  if (uniqueDays[0] !== todayKey && uniqueDays[0] !== yesterdayKey) return 0;

  let streak = 1;
  for (let i = 1; i < uniqueDays.length; i++) {
    const prev = parseKey(uniqueDays[i - 1]);
    const cur = parseKey(uniqueDays[i]);
    const diff = (prev - cur) / 86400000;
    if (diff === 1) streak++;
    else break;
  }
  return streak;
}


const MILESTONES = {
  3: { label: "Three days in a row", note: "your rhythm is finding you.", tone: "gentle" },
  7: { label: "A full week", note: "the field is starting to answer you back.", tone: "warm" },
  14: { label: "Two weeks strong", note: "you're building a real practice now.", tone: "warm" },
  30: { label: "A moon cycle deep", note: "you have your own baseline. Extraordinary.", tone: "high" },
  60: { label: "Sixty days", note: "few people ever get here. You listen well.", tone: "high" },
  100: { label: "One hundred entries in", note: "you are tending an inner map few will ever draw.", tone: "high" },
};


/**
 * Warm celebration banner that appears above the journal timeline when the
 * user has a current streak of ≥ 3 consecutive days. Fires a subtle toast
 * on the day a new milestone is crossed (persisted in localStorage so it
 * only celebrates each milestone once per user).
 */
export default function StreakBanner({ entries }) {
  const streak = useMemo(() => computeStreak(entries), [entries]);
  const lastToastRef = useRef(null);

  useEffect(() => {
    if (streak < 3) return;
    // Only celebrate on a fresh milestone
    const milestone = [100, 60, 30, 14, 7, 3].find((m) => streak === m);
    if (!milestone) return;
    const flagKey = `ep_streak_milestone_${milestone}`;
    try {
      if (localStorage.getItem(flagKey)) return;
      localStorage.setItem(flagKey, String(Date.now()));
    } catch {
      /* ignore */
    }
    if (lastToastRef.current === milestone) return;
    lastToastRef.current = milestone;
    const info = MILESTONES[milestone];
    toast.success(`${info.label} ✨`, {
      description: info.note,
      duration: 6500,
    });
  }, [streak]);

  if (streak < 3) return null;

  // Find the highest milestone reached (for descriptor copy)
  const milestone = [100, 60, 30, 14, 7, 3].find((m) => streak >= m);
  const info = MILESTONES[milestone] || MILESTONES[3];
  const flames = Math.min(3, Math.max(1, Math.floor(streak / 7) + 1));

  return (
    <div
      data-testid="streak-banner"
      className="rounded-2xl border border-[color:var(--rose-gold)]/40 p-4 md:p-5 flex items-center gap-4 mb-6"
      style={{
        background:
          "linear-gradient(135deg, rgba(229, 168, 124, 0.10), rgba(212, 162, 76, 0.06))",
      }}
    >
      <div
        className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center relative"
        style={{ background: "rgba(229, 168, 124, 0.18)" }}
        aria-hidden
      >
        <Flame size={22} className="text-[color:var(--rose-gold)]" />
        {flames > 1 && (
          <span
            className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[color:var(--rose-gold)] flex items-center justify-center text-[10px] font-medium text-[color:var(--bg-base)]"
            data-testid="streak-multiplier"
          >
            ×{flames}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-serif-display text-[color:var(--parchment)] text-xl md:text-2xl leading-none"
            data-testid="streak-count"
          >
            {streak}
          </span>
          <span className="font-serif-italic text-[color:var(--parchment)] text-base md:text-lg">
            day{streak === 1 ? "" : "s"} in a row
          </span>
          <span className="label-overline text-[color:var(--rose-gold)] text-[10px]">· {info.label}</span>
        </div>
        <div className="mt-1 font-serif-italic text-[color:var(--text-secondary)] text-xs md:text-sm">
          {info.note}
        </div>
      </div>
      <Sparkles
        size={14}
        className="text-[color:var(--gold)] flex-shrink-0 hidden sm:inline"
        aria-hidden
      />
    </div>
  );
}
